import React, { useState, useEffect, useCallback } from 'react';
import {
  FiCpu, FiLoader, FiAlertCircle, FiCheckCircle, FiXCircle,
} from 'react-icons/fi';
import { FiBarChart2, FiGrid, FiSettings } from 'react-icons/fi';
import { GiGolfTee } from 'react-icons/gi';
import { useAuth } from './AuthContext';
import BottomNav from './BottomNav';
import PageHeader from './components/PageHeader';
import './PromptLab.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const BOTTOM_NAV = [
  { name: 'Leaderboard', route: '/leaderboard', icon: <FiBarChart2 size={24} />, needsSociety: true },
  { name: 'Rounds',      route: '/tournaments/scorecard-entry', icon: <FiGrid size={24} />, needsSociety: true },
  { name: 'Play Golf',   route: '/play', icon: <GiGolfTee size={24} />, needsSociety: true },
  { name: 'Manage',      route: '/setup', icon: <FiSettings size={24} />, active: true },
];

const TASK_LABELS = {
  discovery: 'Discovery',
  focused_discovery: 'Follow-up research',
  extraction: 'Extraction',
};

const CACHE_KEY = 'promptlab:lastAnalysis';

// The server decides each version's state (routes/prompts.py); this only says
// it in words. Order is the order a person reads a task's list in: what runs,
// what could run next, what is history.
export const STATES = {
  active:      { label: 'In use',         rank: 0 },
  new:         { label: 'New suggestion', rank: 1 },
  used_before: { label: 'Used before',    rank: 2 },
  builtin:     { label: 'Built-in',       rank: 3 },
  out_of_date: { label: 'Out of date',    rank: 4 },
};

export function sortVersions(versions) {
  return [...versions].sort((a, b) =>
    (STATES[a.state]?.rank ?? 9) - (STATES[b.state]?.rank ?? 9) || b.version - a.version);
}

const versionName = (v) => (v.is_builtin ? `Built-in prompt v${v.version}` : `Suggestion v${v.version}`);

// ── Dependency-free LCS line diff ──────────────────────────────────────────
// Returns [{ t: 'same'|'add'|'del', text }] comparing oldText → newText.
function diffLines(oldText, newText) {
  const a = (oldText || '').split('\n');
  const b = (newText || '').split('\n');
  const n = a.length, m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { out.push({ t: 'same', text: a[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ t: 'del', text: a[i] }); i++; }
    else { out.push({ t: 'add', text: b[j] }); j++; }
  }
  while (i < n) out.push({ t: 'del', text: a[i++] });
  while (j < m) out.push({ t: 'add', text: b[j++] });
  return out;
}

function relativeTime(iso) {
  if (!iso) return '';
  const secs = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

export default function PromptLab() {
  const { currentUser } = useAuth();

  const [tasks, setTasks] = useState(null);
  const [inUse, setInUse] = useState({});
  const [metrics, setMetrics] = useState(null);
  const [analysis, setAnalysis] = useState(null);   // { failure_classes, digest, message, rejected_proposals, at }
  const [analyzing, setAnalyzing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewing, setViewing] = useState(null);      // { task, version, template }

  const api = useCallback(async (path, opts = {}) => {
    const token = await currentUser.getIdToken();
    const res = await fetch(`${API}${path}`, {
      ...opts,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
  }, [currentUser]);

  const refresh = useCallback(async () => {
    setError('');
    try {
      const [p, m] = await Promise.all([api('/admin/prompts'), api('/admin/prompts/metrics')]);
      setTasks(p.tasks);
      setInUse(p.in_use || {});
      setMetrics(m.metrics);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (cached) setAnalysis(cached);
    } catch { /* ignore */ }
    refresh();
  }, [refresh]);

  const runCoach = async () => {
    setAnalyzing(true);
    setError('');
    try {
      const result = await api('/admin/prompts/analyze', { method: 'POST' });
      const stored = {
        failure_classes: result.analysis?.failure_classes || [],
        digest: result.digest || null,
        message: result.message || '',
        rejected_proposals: result.rejected_proposals || [],
        stored_proposals: result.stored_proposals || [],
        at: new Date().toISOString(),
      };
      setAnalysis(stored);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(stored)); } catch { /* ignore */ }
      await refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const act = async (id, action, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setError('');
    try {
      await api(`/admin/prompts/${id}/${action}`, { method: 'POST' });
      await refresh();
    } catch (e) {
      setError(e.message);
    }
  };

  const clearAnalysis = () => {
    setAnalysis(null);
    try { localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
  };

  const metricFor = (task, version) =>
    (metrics || []).find(m => m.task_type === task && m.version === version);

  const pendingCount = !tasks ? 0 : Object.values(tasks)
    .flat().filter(v => v.state === 'new').length;

  const digest = analysis?.digest;

  return (
    <div className="pl-page">
      <PageHeader title="Prompt Lab" backTo="/course-downloader" backLabel="Back to Course Downloader" />

      <div className="pl-content">
        <p className="pl-intro">
          The self-improvement loop for the AI course-ingestion prompts. Analyse recent jobs to surface
          recurring issues and get a suggested prompt for each step that could do better. Nothing
          changes until you activate a suggestion.
        </p>

        {error && <div className="pl-error">{error}</div>}

        {/* Run / summary bar */}
        <div className="pl-runbar">
          <button className="pl-btn pl-btn--primary" onClick={runCoach} disabled={analyzing}>
            {analyzing
              ? <><FiLoader className="pl-spin" size={14} /> Analysing recent jobs…</>
              : <><FiCpu size={14} /> Analyse failures &amp; propose improvements</>}
          </button>
          {analysis?.at && (
            <span className="pl-runbar-meta">Last analysed {relativeTime(analysis.at)}</span>
          )}
          {analysis && (
            <button className="pl-link" onClick={clearAnalysis} disabled={analyzing}>Clear results</button>
          )}
        </div>

        {digest && (
          <div className="pl-digest">
            <div className="pl-digest-cell"><strong>{digest.total_analyzed ?? 0}</strong><span>jobs analysed</span></div>
            <div className="pl-digest-cell pl-digest-cell--bad"><strong>{digest.failures ?? 0}</strong><span>failures</span></div>
            <div className="pl-digest-cell pl-digest-cell--warn"><strong>{digest.low_confidence_successes ?? 0}</strong><span>weak results</span></div>
            <div className="pl-digest-cell pl-digest-cell--good"><strong>{digest.high_confidence_success_count ?? 0}</strong><span>strong results</span></div>
          </div>
        )}
        {analysis?.message && <p className="pl-note">{analysis.message}</p>}

        {/* Issues */}
        {analysis && (analysis.failure_classes || []).length > 0 && (
          <section className="pl-section">
            <h2 className="pl-section-title"><FiAlertCircle size={15} /> Issues found</h2>
            <div className="pl-issues">
              {analysis.failure_classes.map((fc, i) => (
                <div key={i} className="pl-issue">
                  <span className="pl-issue-count">{fc.count}×</span>
                  <div className="pl-issue-body">
                    <div className="pl-issue-cause">{fc.cause}</div>
                    <div className="pl-issue-rec">{fc.recommendation}</div>
                  </div>
                  <span className={`pl-chip ${fc.addressable_by_prompt ? 'pl-chip--fixable' : 'pl-chip--manual'}`}>
                    {fc.addressable_by_prompt ? 'fixable by prompt' : 'needs manual fix'}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Rejected proposals from the latest run */}
        {analysis && (analysis.rejected_proposals || []).length > 0 && (
          <section className="pl-section">
            <h2 className="pl-section-title"><FiXCircle size={15} /> Rejected proposals</h2>
            <div className="pl-issues">
              {analysis.rejected_proposals.map((rp, i) => (
                <div key={i} className="pl-issue pl-issue--rejected">
                  <div className="pl-issue-body">
                    <div className="pl-issue-cause">{TASK_LABELS[rp.task_type] || rp.task_type}</div>
                    <div className="pl-issue-rec">{(rp.problems || []).join('; ')}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* What runs, and the suggestions */}
        <section className="pl-section">
          <h2 className="pl-section-title"><FiCpu size={15} /> Prompts in use &amp; suggestions</h2>
          <p className="pl-note pl-note--block">
            Each step uses exactly one prompt: the one marked <strong>In use</strong>. A suggestion
            changes nothing until you activate it. A suggestion written for a prompt that is no
            longer in use is <strong>out of date</strong>: it would undo the changes made since,
            so it can only be dismissed.
          </p>
          {loading && <div className="pl-loading">Loading…</div>}
          {!loading && !tasks && !error && <div className="pl-loading">No prompt data.</div>}

          {tasks && Object.entries(tasks).map(([task, versions]) => {
            const current = inUse[task];
            const activeTemplate = (versions.find(v => v.state === 'active') || {}).template || '';
            const taskName = TASK_LABELS[task] || task;
            return (
              <div key={task} className="pl-task">
                <div className="pl-task-name">{taskName}</div>
                {current && (
                  <div className="pl-inuse">
                    <FiCheckCircle size={13} />
                    <span>In use: <strong>{current.label}</strong>
                      {current.since && <span className="pl-inuse-since"> · since {relativeTime(current.since)}</span>}
                    </span>
                    {current.id != null && (
                      <button className="pl-link" onClick={() => act(current.id, 'deactivate',
                        `Switch ${taskName} back to the built-in prompt? New downloads will use it from now on.`)}>
                        Use built-in prompt
                      </button>
                    )}
                  </div>
                )}
                {sortVersions(versions).map(v => {
                  const m = metricFor(task, v.version);
                  const meta = STATES[v.state] || { label: v.state };
                  const showDiff = v.can_activate;
                  return (
                    <div key={v.id ?? 'builtin'}
                         className={`pl-version pl-version--${v.state}`}>
                      <div className="pl-version-head">
                        <span className="pl-vnum">{versionName(v)}</span>
                        <span className={`pl-vstatus pl-vstatus--${v.state}`}>{meta.label}</span>
                        <span className="pl-vmeta">
                          {m
                            ? `${m.jobs} job${m.jobs !== 1 ? 's' : ''} · ${Math.round((m.success_rate || 0) * 100)}% ok${m.avg_confidence != null ? ` · conf ${m.avg_confidence}` : ''}`
                            : 'no jobs yet'}
                        </span>
                        <span className="pl-vactions">
                          <button className="pl-link" onClick={() => setViewing({ task, name: versionName(v), template: v.template })}>View full</button>
                          {v.can_activate && (
                            <button className="pl-link pl-link--go" onClick={() => act(v.id, 'activate',
                              `Use ${versionName(v)} for ${taskName} from now on? New downloads will use it; you can switch back at any time.`)}>
                              Activate
                            </button>
                          )}
                          {!v.is_builtin && v.state !== 'active' && (
                            <button className="pl-link" onClick={() => act(v.id, 'dismiss',
                              `Dismiss ${versionName(v)}? It will no longer be listed.`)}>
                              Dismiss
                            </button>
                          )}
                        </span>
                      </div>

                      {v.state === 'out_of_date' && (
                        <p className="pl-stale">
                          Written for a prompt that is no longer in use, so activating it would undo the
                          changes made since. Run a new analysis for a suggestion based on the current prompt.
                        </p>
                      )}
                      {showDiff && v.notes && <p className="pl-rationale">{v.notes}</p>}
                      {showDiff && (
                        <div className="pl-proposal">
                          <div className="pl-diff-label">Changes vs the prompt in use</div>
                          <pre className="pl-diff">
                            {diffLines(activeTemplate, v.template).map((ln, k) => (
                              <div key={k} className={`pl-diff-line pl-diff-line--${ln.t}`}>
                                <span className="pl-diff-gutter">{ln.t === 'add' ? '+' : ln.t === 'del' ? '−' : ' '}</span>
                                {ln.text || ' '}
                              </div>
                            ))}
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
          {!loading && tasks && pendingCount === 0 && (
            <p className="pl-note"><FiCheckCircle size={13} /> No new suggestions. Run an analysis to look for improvements.</p>
          )}
        </section>
      </div>

      {viewing && (
        <div className="pl-modal-overlay" onClick={() => setViewing(null)}>
          <div className="pl-modal" onClick={e => e.stopPropagation()}>
            <div className="pl-modal-head">
              <strong>{TASK_LABELS[viewing.task] || viewing.task} · {viewing.name}</strong>
              <button className="pl-link" onClick={() => setViewing(null)}>close</button>
            </div>
            <pre className="pl-template">{viewing.template}</pre>
          </div>
        </div>
      )}

      <BottomNav items={BOTTOM_NAV} />
    </div>
  );
}
